import { useState, useEffect } from 'react';
import { Table, Skeleton, Switch, Drawer, Form, Flex, Empty, Spin, message, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import FormButton from '../common/formbutton';
import RecipeItemForm from './recipeitemform';
import ImageWithSpinner from '../content/imagewithspinner';
import CancelButton from '../common/cancelbutton';
import { useAuth } from '../../auth/authcontext';
import { getRecipesForMenu, updateRecipe } from '../../services/menuservice';

interface MenuItem {
  guid: string;
}

interface RecipeItem {
  guid: string;
  menuRecipeLinkGuid: string;
  name?: string;
  prepOrder?: number;
  itemNumber?: number;
  jdeProductId?: string;
  active?: boolean;
  imageName?: string;
  measure?: string;
  quantity?: number;
  customMeasure?: string;
  customQuantity?: number;
  recipeKdsInstructions?: string;
  fryer?: boolean;
  required?: boolean;
  showJdeProductIdColumn: boolean;
}

interface RecipeItemsProps {
  menuItem: MenuItem;
}

const RecipeItems: React.FC<RecipeItemsProps> = ({ menuItem }) => {
  
  const [loading, setLoading] = useState<boolean>(true);
  const menuItemGuid = menuItem.guid;
  const [recipeItems, setRecipeItems] = useState<RecipeItem[]>([]);
  const [selectedRecipeItem, setSelectedRecipeItem] = useState<RecipeItem | null>(null);
  const [editDrawerVisisble, setEditDrawerVisible] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  const [recipeItemForm] = Form.useForm();
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
  const [messageApi, contextHolder] = message.useMessage();
  const { userId } = useAuth();
  const userGuid = userId || null;

  useEffect(() => {
    const fetchRecipesForMenuItem = async () => {
      try {
        const data = await getRecipesForMenu(menuItemGuid);
        setRecipeItems(data);
      } catch (error) {
        console.error('Error fetching All recipe items.', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecipesForMenuItem();
  }, [ menuItemGuid, selectedRecipeItem]);

  const showJdeProductColumn = recipeItems.length > 0
    && recipeItems[0].showJdeProductIdColumn;


  const columns: ColumnsType<RecipeItem> = [
    {
      title: '#Order',
      dataIndex: 'prepOrder',
      key: 'prepOrder',
      sorter: (a, b) => (a.prepOrder || 0) - (b.prepOrder || 0),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },

    ...(showJdeProductColumn
      ? [
        {
          title: 'Product Id',
          dataIndex: 'jdeProductId',
          key: 'jdeProductId',
        },
      ]
      : [
        {
          title: 'Item Number',
          dataIndex: 'itemNumber',
          key: 'itemNumber',
          sorter: (a: RecipeItem, b: RecipeItem) =>
            (a.itemNumber || 0) - (b.itemNumber || 0),
        },
      ]),
    {
      title: 'Measure',
      dataIndex: 'measure',
      key: 'measure',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Custom Measure',
      dataIndex: 'customMeasure',
      key: 'customMeasure',
      render: (value: string | undefined | null) =>
        value ? value : <Tag color="gold">Not Set</Tag>, //yellow tag
    },
    {
      title: 'Custom Quantity',
      dataIndex: 'customQuantity',
      key: 'customQuantity',
      render: (value: number | undefined | null) =>
        value !== undefined && value !== null
          ? value.toFixed(2) // always show 2 decimals
          : <Tag color="gold">Not Set</Tag>,
    },
    {
      title: 'Fryer',
      dataIndex: 'fryer',
      key: 'fryer',
      render: (fryer: boolean) => <Switch checked={fryer} size="small" disabled />,
    },
    {
      title: 'Required',
      dataIndex: 'required',
      key: 'required',
      render: (required: boolean) => <Switch checked={required} size="small" disabled />,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <FormButton
          title="Edit"
          onClick={() => handleEdit(record)}
          tooltip={"Edit"}
        />
      ),
    },
  ];

  const handleFormValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues.imageName !== undefined) {
      setPreviewImageUrl(changedValues.imageName);
    }
  };

  const handleEdit = async (recipeItem: RecipeItem) => {
    if (recipeItem) {
      recipeItemForm.resetFields();
      recipeItemForm.setFieldsValue({
        imageName: recipeItem.imageName,
        //recipeKdsInstructions: recipeItem.recipeKdsInstructions,
        recipeKdsInstructions: recipeItem.recipeKdsInstructions
          ? recipeItem.recipeKdsInstructions.split('\n').map(i => ({ instruction: i }))
          : [],

        customQuantity:
          recipeItem.customQuantity !== undefined && recipeItem.customQuantity !== null
            ? Number(recipeItem.customQuantity).toFixed(2)
            : undefined,
        quantity: recipeItem.quantity, // read-only
        customMeasure: recipeItem.customMeasure,
        measure: recipeItem.measure,   // read-only
        fryer: recipeItem.fryer || false,
        required: recipeItem.required || false,
      });

    }
    setPreviewImageUrl(recipeItem.imageName || '');
    setSelectedRecipeItem(recipeItem);
    setEditDrawerVisible(true);
  };

  const handleEditDrawerClose = () => {
    setEditDrawerVisible(false);
  };

  const handleSaveRecipe = async (values: any) => {
    setSaveLoading(true);
    const normalizedQuantity =
      values.customQuantity === undefined ||
        values.customQuantity === null ||
        values.customQuantity === ''
        ? null
        : parseFloat(values.customQuantity);

    const recipeUpdateData = {
      guid: selectedRecipeItem?.guid,
      menuRecipeLinkGuid: selectedRecipeItem?.menuRecipeLinkGuid,
      menuGuid: menuItemGuid,
      createdBy: userGuid,
      imageName: values.imageName,
      //recipeKdsInstructions: values.recipeKdsInstructions,
      recipeKdsInstructions: values.recipeKdsInstructions
        ? values.recipeKdsInstructions.map((i: any) => i.instruction).join('\n')
        : null,

      customQuantity: normalizedQuantity,
      customMeasure: values.customMeasure,
      fryer: values.fryer || false,
      required: values.required || false,
    };

    await updateRecipe(recipeUpdateData)
    .then(() => {
      successMessage();
    })
    .catch(error => {
      console.error('There was an error updating the menu item!', error);
      errorMessage();
    });
  
    setSaveLoading(false);
    setEditDrawerVisible(false);
    setSelectedRecipeItem(null);
  };

  const formFooter = (
    <div style={{ textAlign: 'right' }}>
      <CancelButton onClick={handleEditDrawerClose} />
      <FormButton
        title="Save"
        onClick={async () => {
          try {
            const values = await recipeItemForm.validateFields();
            await handleSaveRecipe(values);
          } catch (err) {
            console.log('Validation Failed:', err);
          }
        }}
        tooltip="Save"
      />
    </div>
  );

  const successMessage = () => {
    messageApi.success("Recipe updated successfully.");
  };

  const errorMessage = () => {
    messageApi.error("Something went wrong while updating recipe.");
  };

  return (
    <>
      {contextHolder}
      <div>
        {loading ? (
          <Skeleton active />
        ) : (
          <Table
            dataSource={recipeItems}
            columns={columns}
            rowKey="guid"
            pagination={false}
          />
        )}
      </div>
      <Drawer
        title={`${selectedRecipeItem?.name}`}
        placement="right"
        closable={true}
        onClose={handleEditDrawerClose}
        open={editDrawerVisisble}
        width={500}
        styles={{
          header: {
            color: 'white',
            backgroundColor: 'var(--color-primary)',
            fontWeight: 350,
            fontSize: '14px',
            border: 0,
            minHeight: '40px'
          }
        }}
      >
        {selectedRecipeItem && (
          <Spin tip="Updating..." spinning={saveLoading}>
            <Flex justify='center' style={{ marginBottom: '10px' }}>
              <div style={{ width: '180px', height: '180px' }}>
                {previewImageUrl ? (
                  <ImageWithSpinner imageUrl={previewImageUrl} />
                ) : (
                  <Empty description="Image Not Added" />
                )}
              </div>
            </Flex>
            <RecipeItemForm
              form={recipeItemForm}
              onCancel={handleEditDrawerClose}
              onSave={handleSaveRecipe}          // ✅ wired directly
              onValuesChange={handleFormValuesChange}
            />
          </Spin>
        )}
      </Drawer>
    </>
  );
};

export default RecipeItems;